import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, Bell, CheckCheck, ArrowRight, Loader2 } from 'lucide-react';
import api from '../../../utils/api';

/**
 * NotificationFunnelWidget — three-column KPI mini-flow that turns the
 * two existing analytics endpoints into a visible "growth" story:
 *
 *     In DACSAI  →  Opted in to push  →  Delivered last 30d
 *
 * Backed by:
 *   - GET /api/retailer/analytics/deal-reach       (live)
 *   - GET /api/retailer/analytics/push-open-rate   (30-day)
 *
 * No new backend. Both endpoints are DRLP-scoped and quick.
 */
export default function NotificationFunnelWidget() {
  const [reach, setReach] = useState(null);
  const [openRate, setOpenRate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          api.get('/retailer/analytics/deal-reach'),
          api.get('/retailer/analytics/push-open-rate', { params: { days: 30 } }),
        ]);
        if (alive) {
          setReach(r1.data);
          setOpenRate(r2.data);
        }
      } catch (e) {
        // Silent — both endpoints are decorative on the dashboard.
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const inDacsai = reach?.reachable_dacs ?? 0;
  const optedIn = reach?.with_push_tokens ?? 0;
  const delivered = openRate?.sent ?? 0;

  return (
    <Card data-testid="notification-funnel-widget">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Bell className="w-4 h-4 text-emerald-600" />
          Notification funnel
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Grow left→right: bigger DACSAI → more customers → more opt-ins → more sales
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading funnel…
          </div>
        ) : (
          <div className="flex items-center justify-between gap-2">
            <Step
              icon={<Users className="w-5 h-5 text-slate-500" />}
              label="In DACSAI"
              value={inDacsai}
              hint="Customers in your service area"
              testid="funnel-step-dacsai"
            />
            <Arrow />
            <Step
              icon={<Bell className="w-5 h-5 text-emerald-500" />}
              label="Push opt-in"
              value={optedIn}
              hint={inDacsai > 0 ? `${Math.round((optedIn / inDacsai) * 100)}% of DACSAI` : '—'}
              testid="funnel-step-optin"
            />
            <Arrow />
            <Step
              icon={<CheckCheck className="w-5 h-5 text-emerald-600" />}
              label="Delivered · 30d"
              value={delivered}
              hint={openRate ? `${openRate.delivery_success_rate}% success rate` : '—'}
              testid="funnel-step-delivered"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Step({ icon, label, value, hint, testid }) {
  return (
    <div className="flex-1 text-center min-w-0" data-testid={testid}>
      <div className="flex justify-center mb-1">{icon}</div>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
      <p className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">{label}</p>
      <p className="text-[11px] text-slate-400 mt-0.5 truncate">{hint}</p>
    </div>
  );
}

function Arrow() {
  return (
    <ArrowRight className="w-4 h-4 text-slate-300 shrink-0" aria-hidden />
  );
}
